/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { Typography } from '@mui/material';
import { Dayjs } from 'dayjs';

import { ContactPerson, ActivityType, ActivityStatus, Campaign, Lookup, Activity, CampaignActivity } from '../../api/types/types';
import TestIds from '../../../../testIds';
import Modal from '../../../../components/Modal';
import ActivityModalActionBar, {
  ActivityModalActionBarProps,
} from '../ActivityModalActionBar/ActivityModalActionBar';
import CreateOrUpdateActivityModalStep1 from './CreateOrUpdateActivityModalStep1';
import CreateOrUpdateActivityModalStep2 from './CreateOrUpdateActivityModalStep2';

export interface CreateOrEditActivityModalProps {
  open: boolean;
  handleClose: () => void;
  contactPersons?: ContactPerson[];
  activityBarProps: ActivityModalActionBarProps;
  control: Control<CreateOrEditFormValues>;
  validateActivityForm?: () => Promise<boolean>;
  title: string;
  subtitle: string;
  error?: string;
  owner: string;
  disableActivityTypeSelect?: boolean;
  accountNumber?: string;
  campaigns?: Campaign[];
  lookups?: Lookup[];
  activity?: Activity;
  activityName?: string;
  onStep2Complete?: (campaignResponses: CampaignActivity[]) => Promise<void>;
}

export interface CreateOrEditFormValues {
  activityStatus: ActivityStatus;
  activityType: ActivityType | string;
  subject: string;
  description: string;
  scheduleDate?: Dayjs;
  contactId: string;
}

const CreateOrEditActivityModal: FC<CreateOrEditActivityModalProps> = ({
  open,
  handleClose,
  contactPersons = [],
  control,
  validateActivityForm,
  owner,
  error,
  title,
  subtitle,
  activityBarProps,
  disableActivityTypeSelect,
  campaigns,
  lookups,
  activityName,
  onStep2Complete,
}): JSX.Element => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const activityStatus = useWatch({ control, name: 'activityStatus' });

  const hasCampaigns = (campaigns?.length ?? 0) > 0;
  const shouldShowStep2 = activityStatus === ActivityStatus.Completed && hasCampaigns;

  const resetForm = () => {
    setCurrentStep(1);
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
  };

  const handleSubmitClick = async () => {
    if (currentStep === 1 && shouldShowStep2) {
      if (!validateActivityForm || (await validateActivityForm())) {
        setCurrentStep(2);
      }
    } else {
      activityBarProps.onSubmit();
    }
  };

  const handleCloseClick = () => {
    resetForm();
    activityBarProps.onClose();
  };

  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  const getModalTitle = (): string => {
    return currentStep === 1 ? title : 'Complete Activity';
  };

  const getModalHeader = (): JSX.Element | null => {
    if (currentStep === 1) {
      return (
        <Typography variant='body2' color='shade100' fontSize='1.125rem'>
          {subtitle}
        </Typography>
      );
    }

    if (hasCampaigns) {
      return (
        <Typography variant='body2' color='text.secondary'>
          Track the campaigns you discussed and add response details
        </Typography>
      );
    }

    return null;
  };

  const getStep1ActionBarProps = () => ({
    ...activityBarProps,
    onSubmit: handleSubmitClick,
    onClose: handleCloseClick,
    loadingIndicatorText: activityStatus === ActivityStatus.Completed
      ? "Completing activity"
      : activityBarProps.loadingIndicatorText,
    submitButtonText: shouldShowStep2
      ? 'Next'
      : activityBarProps.submitButtonText,
    submitButtonTestId: TestIds.feat.activities.createOrEditActivityModal.submitButton,
    closeButtonTestId: TestIds.feat.activities.createOrEditActivityModal.cancelButton,
    loadingIndicatorTestId: TestIds.feat.activities.createOrEditActivityModal.loading,
  });

  return (
    <form>
      <Modal
        title={getModalTitle()}
        open={open}
        handleClose={handleModalClose}
        maxWidth='md'
        fullWidth
        data-testid={TestIds.feat.activities.createOrEditActivityModal.component}
        header={getModalHeader()}
        disableBackdropClose={true}
      >
        {currentStep === 1 ? (
          <>
            <CreateOrUpdateActivityModalStep1
              control={control}
              contactPersons={contactPersons}
              owner={owner}
              disableActivityTypeSelect={disableActivityTypeSelect}
              error={error}
            />
            <ActivityModalActionBar {...getStep1ActionBarProps()} />
          </>
        ) : (
          <CreateOrUpdateActivityModalStep2
            campaigns={campaigns ?? []}
            lookups={lookups}
            activityName={activityName}
            onBack={handleBackToStep1}
            onStep2Complete={onStep2Complete}
            onSubmitFallback={activityBarProps.onSubmit}
            open={open}
            showLoadingIndicator={activityBarProps.showLoadingIndicator}
            loadingIndicatorText={activityBarProps.loadingIndicatorText}
          />
        )}
      </Modal>
    </form>
  );
};

export default CreateOrEditActivityModal;
